import { createContext, useContext, useEffect, useState } from 'react';
import { STRINGS, interp } from './strings.js';
import { setFormatLang } from './format.js';

const I18nCtx = createContext(null);
// Its own key, not the admin dashboard's: the two apps share an origin in
// production and a parent's language choice has nothing to do with a
// content manager's.
const LS_KEY = 'rofoofee.portal.lang';
// Arabic first, matching rofoofee.com itself (English lives under /en there).
const DEFAULT_LANG = 'ar';
const AVAILABLE = ['ar', 'en'];

function readStored() {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v && AVAILABLE.includes(v)) return v;
  } catch {}
  return DEFAULT_LANG;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(readStored);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    setFormatLang(lang);
    try { localStorage.setItem(LS_KEY, lang); } catch {}
  }, [lang]);

  const setLang = (next) => {
    if (!AVAILABLE.includes(next)) return;
    setLangState(next);
  };

  function lookup(path, dict) {
    const parts = path.split('.');
    let cur = dict;
    for (const p of parts) {
      if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
      else return null;
    }
    return typeof cur === 'string' ? cur : null;
  }

  function t(key, vars) {
    const hit = lookup(key, STRINGS[lang]) ?? lookup(key, STRINGS.en);
    if (hit == null) return key;
    return vars ? interp(hit, vars) : hit;
  }

  const value = {
    lang,
    setLang,
    t,
    dir: lang === 'ar' ? 'rtl' : 'ltr',
    isRtl: lang === 'ar',
    available: AVAILABLE,
  };
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  const v = useContext(I18nCtx);
  if (!v) throw new Error('useI18n must be used inside I18nProvider');
  return v;
}

/** Convenience hook for components that only need t(). */
export function useT() {
  return useI18n().t;
}
